import React from "react";
import NoteIcon from "../common/NoteIcon";
import {
  calculateRegularTotalHours,
  formatDisplayDate,
  formatShortMonthDay,
  formatHours,
  getHospitalDepartments,
  getRegularTotalHourStep,
  getRowLabelDate,
  getTotal
} from "../../utils/timecard";
import { useAutoExpandDayCard } from "../../hooks/useAutoExpandDayCard";
import { useTimeFormat } from "../../hooks/useTimeFormat";
import Field from "../common/Field";
import TimeCell from "../common/TimeCell";
import DepartmentSelect from "./DepartmentSelect";
import ChevronIcon from "../common/ChevronIcon";
import BreakApproverDialog from "./BreakApproverDialog";
import NotesDialog from "./NotesDialog";

function RegularHoursTable({
  rows,
  onChange,
  onTimeChange,
  onTookBreakChange,
  onLateBreakChange,
  hospitalOptions,
  weekStartDate,
  readOnly,
  timeFormat
}) {
  const [notesIndex, setNotesIndex] = React.useState(null);

  return (
    <>
      <div className="table-scroll">
        <table className="time-entry-table regular-table">
          <thead>
            <tr>
              <th scope="col">Day</th>
              <th scope="col">Hospital</th>
              <th scope="col">Dept</th>
              <th scope="col">Time in</th>
              <th scope="col">Break start</th>
              <th scope="col">Break end</th>
              <th scope="col">Time out</th>
              <th scope="col" className="hours-column">Hours</th>
              <th scope="col">Missed break</th>
              <th scope="col">Late break</th>
              <th scope="col">Flexed</th>
              <th scope="col">Voluntary Flex</th>
              <th scope="col">Notes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const isContinuation = index > 0 && rows[index - 1].day === row.day;
              return (
              <tr key={`${row.day}-${index}`} className={isContinuation ? "regular-row-continuation" : ""}>
                <th scope="row">
                  {isContinuation ? (
                    <div className="day-date-cell day-date-cell--continuation" aria-label={`${row.day} (continued)`} />
                  ) : (
                    <div className="day-date-cell">
                      <span className="weekday">{row.day}</span>
                      {getRowLabelDate(row.date, weekStartDate, row.day)
                        ? <span className="date">{formatShortMonthDay(getRowLabelDate(row.date, weekStartDate, row.day))}</span>
                        : null}
                    </div>
                  )}
                </th>
                <td>
                  <select
                    aria-label={`${row.day} hospital`}
                    value={row.hospital}
                    disabled={readOnly}
                    onChange={(event) => onChange(index, "hospital", event.target.value)}
                  >
                    <option value="">Select hospital</option>
                    {hospitalOptions.map((hospital) => (
                      <option key={hospital.hospitalId} value={hospital.name}>
                        {hospital.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <DepartmentSelect
                    ariaLabel={`${row.day} department`}
                    departments={getHospitalDepartments(hospitalOptions, row.hospital)}
                    value={row.department}
                    disabled={readOnly}
                    onChange={(value) => onChange(index, "department", value)}
                  />
                </td>
                <td>
                  <TimeCell
                    value={row.timeIn}
                    format={timeFormat}
                    readOnly={readOnly}
                    ariaLabel={`${row.day} time in`}
                    onChange={(value) => onTimeChange(index, "timeIn", value)}
                  />
                </td>
                <td>
                  <TimeCell
                    value={row.breakStart}
                    format={timeFormat}
                    readOnly={readOnly}
                    ariaLabel={`${row.day} break start`}
                    onChange={(value) => onTimeChange(index, "breakStart", value)}
                  />
                </td>
                <td>
                  <TimeCell
                    value={row.breakEnd}
                    format={timeFormat}
                    readOnly={readOnly}
                    ariaLabel={`${row.day} break end`}
                    onChange={(value) => onTimeChange(index, "breakEnd", value)}
                  />
                </td>
                <td>
                  <TimeCell
                    value={row.timeOut}
                    format={timeFormat}
                    readOnly={readOnly}
                    ariaLabel={`${row.day} time out`}
                    onChange={(value) => onTimeChange(index, "timeOut", value)}
                  />
                </td>
                <td className="hours-column">
                  <input
                    type="number"
                    min="0"
                    step={getRegularTotalHourStep(row.hospital, hospitalOptions)}
                    aria-label={`${row.day} total hours`}
                    value={row.totalHours}
                    readOnly
                    disabled={readOnly}
                    onChange={(event) =>
                      onChange(index, "totalHours", event.target.value)
                    }
                  />
                </td>
                <td className="check-cell">
                  <input
                    type="checkbox"
                    aria-label={`${row.day} missed break`}
                    checked={row.tookBreak}
                    disabled={readOnly}
                    onChange={(event) => onTookBreakChange(index, event.target.checked)}
                  />
                </td>
                <td className="check-cell">
                  <input
                    type="checkbox"
                    aria-label={`${row.day} late break`}
                    checked={row.lateBreak}
                    disabled={readOnly}
                    onChange={(event) => onLateBreakChange(index, event.target.checked)}
                  />
                </td>
                <td className="check-cell">
                  <input
                    type="checkbox"
                    aria-label={`${row.day} flexed`}
                    checked={row.flexed}
                    disabled={readOnly}
                    onChange={(event) => onChange(index, "flexed", event.target.checked)}
                  />
                </td>
                <td className="check-cell">
                  <input
                    type="checkbox"
                    aria-label={`${row.day} voluntarily flexed`}
                    checked={row.volFlex}
                    disabled={readOnly}
                    onChange={(event) => onChange(index, "volFlex", event.target.checked)}
                  />
                </td>
                <td className="notes-action-cell">
                  {(!readOnly || row.notes) && (
                    <button
                      type="button"
                      className={`notes-action-button${row.notes ? " has-note" : ""}`}
                      aria-label={`${row.notes ? "View or edit note" : "Add note"} for ${row.day}`}
                      onClick={() => setNotesIndex(index)}
                    >
                      <NoteIcon />
                    </button>
                  )}
                </td>
              </tr>
            ); })}
          </tbody>
          <tfoot>
            <tr className="weekly-total-row">
              <td colSpan="7" className="weekly-total-label">Weekly Total</td>
              <td className="hours-column weekly-total-value">{formatHours(getTotal(rows))}</td>
              <td colSpan="5"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <NotesDialog
        titleId="notes-dialog-title"
        rows={rows}
        notesIndex={notesIndex}
        readOnly={readOnly}
        onChange={onChange}
        onClose={() => setNotesIndex(null)}
      />
    </>
  );
}

function DayCardFields({ row, index, onChange, onTimeChange, onTookBreakChange, onLateBreakChange, hospitalOptions, readOnly, timeFormat }) {
  return (
    <div className="day-card-fields">
      <Field id={`regular-${index}-hospital`} label="Hospital">
        <select id={`regular-${index}-hospital`} value={row.hospital} disabled={readOnly}
          onChange={(e) => onChange(index, "hospital", e.target.value)}>
          <option value="">Select hospital</option>
          {hospitalOptions.map((h) => (
            <option key={h.hospitalId} value={h.name}>{h.name}</option>
          ))}
        </select>
      </Field>
      <Field id={`regular-${index}-dept`} label="Dept" compact>
        <DepartmentSelect id={`regular-${index}-dept`} ariaLabel={`${row.day} department`}
          departments={getHospitalDepartments(hospitalOptions, row.hospital)}
          value={row.department} disabled={readOnly}
          onChange={(value) => onChange(index, "department", value)} />
      </Field>
      <div className="field-pair">
        <Field id={`regular-${index}-time-in`} label="Time in">
          <TimeCell id={`regular-${index}-time-in`} value={row.timeIn} format={timeFormat}
            readOnly={readOnly} ariaLabel={`${row.day} time in`}
            onChange={(v) => onTimeChange(index, "timeIn", v)} />
        </Field>
        <Field id={`regular-${index}-time-out`} label="Time out">
          <TimeCell id={`regular-${index}-time-out`} value={row.timeOut} format={timeFormat}
            readOnly={readOnly} ariaLabel={`${row.day} time out`}
            onChange={(v) => onTimeChange(index, "timeOut", v)} />
        </Field>
      </div>
      <div className="field-pair">
        <Field id={`regular-${index}-break-start`} label="Break start">
          <TimeCell id={`regular-${index}-break-start`} value={row.breakStart} format={timeFormat}
            readOnly={readOnly} ariaLabel={`${row.day} break start`}
            onChange={(v) => onTimeChange(index, "breakStart", v)} />
        </Field>
        <Field id={`regular-${index}-break-end`} label="Break end">
          <TimeCell id={`regular-${index}-break-end`} value={row.breakEnd} format={timeFormat}
            readOnly={readOnly} ariaLabel={`${row.day} break end`}
            onChange={(v) => onTimeChange(index, "breakEnd", v)} />
        </Field>
      </div>
      <Field id={`regular-${index}-hours`} label="Total hours">
        <input id={`regular-${index}-hours`} type="number" min="0"
          step={getRegularTotalHourStep(row.hospital, hospitalOptions)}
          value={row.totalHours} disabled={readOnly}
          onChange={(e) => onChange(index, "totalHours", e.target.value)} />
      </Field>
      <div className="toggle-field-pair">
        <label className="toggle-field" htmlFor={`regular-${index}-missed-break`}>
          <span>Missed break</span>
          <input id={`regular-${index}-missed-break`} type="checkbox" checked={row.tookBreak}
            disabled={readOnly} onChange={(e) => onTookBreakChange(index, e.target.checked)} />
        </label>
        <label className="toggle-field" htmlFor={`regular-${index}-late-break`}>
          <span>Late break</span>
          <input id={`regular-${index}-late-break`} type="checkbox" checked={row.lateBreak}
            disabled={readOnly} onChange={(e) => onLateBreakChange(index, e.target.checked)} />
        </label>
        <label className="toggle-field" htmlFor={`regular-${index}-flexed`}>
          <span>Flexed</span>
          <input id={`regular-${index}-flexed`} type="checkbox" checked={row.flexed}
            disabled={readOnly} onChange={(e) => onChange(index, "flexed", e.target.checked)} />
        </label>
        <label className="toggle-field" htmlFor={`regular-${index}-vol-flex`}>
          <span>Voluntary Flex</span>
          <input id={`regular-${index}-vol-flex`} type="checkbox" checked={row.volFlex}
            disabled={readOnly} onChange={(e) => onChange(index, "volFlex", e.target.checked)} />
        </label>
      </div>
      <Field id={`regular-${index}-notes`} label="Notes" full>
        <textarea id={`regular-${index}-notes`} rows="2" value={row.notes} disabled={readOnly}
          onChange={(e) => onChange(index, "notes", e.target.value)} />
      </Field>
    </div>
  );
}

function RegularMobileCards({
  rows,
  onChange,
  onTimeChange,
  onTookBreakChange,
  onLateBreakChange,
  hospitalOptions,
  weekStartDate,
  readOnly,
  setDayCardRef,
  timeFormat
}) {
  const groups = React.useMemo(() => {
    const map = new Map();
    const order = [];
    rows.forEach((row, index) => {
      if (!map.has(row.day)) {
        const group = { day: row.day, entries: [] };
        map.set(row.day, group);
        order.push(group);
      }
      map.get(row.day).entries.push({ row, index });
    });
    return order;
  }, [rows]);

  const fieldProps = { onChange, onTimeChange, onTookBreakChange, onLateBreakChange, hospitalOptions, readOnly, timeFormat };

  return (
    <div className="mobile-entry-list" aria-label="Regular hours by day">
      {groups.map(({ day, entries }) => {
        const groupHours = entries.reduce((sum, e) => sum + (Number.parseFloat(e.row.totalHours) || 0), 0);
        const isEmpty = groupHours === 0;
        const firstDate = getRowLabelDate(entries[0].row.date, weekStartDate, day);
        const dateLabel = firstDate ? `${day} — ${formatDisplayDate(firstDate)}` : day;

        if (entries.length === 1) {
          const { row, index } = entries[0];
          return (
            <details key={`${day}-0`}
              className={`day-card${isEmpty ? " day-card--empty" : ""}`}
              ref={(el) => setDayCardRef(day, el)}>
              <summary>
                <ChevronIcon />
                <div className="day-card-summary-content">
                  <span className="day-card-date">{dateLabel}</span>
                  {row.hospital && <span className="day-card-summary-hospital">{row.hospital}</span>}
                </div>
                <strong className={isEmpty ? "day-card-hours--empty" : undefined}>
                  {formatHours(Number.parseFloat(row.totalHours) || 0)}
                </strong>
              </summary>
              <DayCardFields row={row} index={index} {...fieldProps} />
            </details>
          );
        }

        return (
          <div key={day}
            className={`day-card-group${isEmpty ? " day-card-group--all-empty" : ""}`}
            ref={(el) => setDayCardRef(day, el)}>
            <div className="day-card-group-header">
              <span className="day-card-date">{dateLabel}</span>
              <strong className={isEmpty ? "day-card-hours--empty" : undefined}>
                {formatHours(groupHours)}
              </strong>
            </div>
            {entries.map(({ row, index }) => {
              const rowIsEmpty = !Number.parseFloat(row.totalHours);
              return (
                <details key={`${day}-${index}`}
                  className={`day-card day-card--sub${rowIsEmpty ? " day-card--empty" : ""}`}>
                  <summary>
                    <ChevronIcon />
                    <div className="day-card-summary-content">
                      <span className="day-card-date day-card-date--sub">
                        {row.hospital || <em className="day-card-no-hospital">No hospital selected</em>}
                      </span>
                    </div>
                    <strong className={rowIsEmpty ? "day-card-hours--empty" : undefined}>
                      {formatHours(Number.parseFloat(row.totalHours) || 0)}
                    </strong>
                  </summary>
                  <DayCardFields row={row} index={index} {...fieldProps} />
                </details>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

export default function RegularHoursSection({
  rows,
  onChange,
  onTookBreakChange,
  hospitalOptions = [],
  weekStartDate,
  readOnly = false
}) {
  const { setDayCardRef } = useAutoExpandDayCard();
  const { timeFormat } = useTimeFormat();
  const [missedBreakPending, setMissedBreakPending] = React.useState(null);
  const [lateBreakPending, setLateBreakPending] = React.useState(null);

  const handleRegularTimeChange = (rowIndex, field, value) => {
    onChange(rowIndex, field, value);
  };

  const handleRegularTookBreakChange = (rowIndex, tookBreak) => {
    const updatedRow = { ...rows[rowIndex], tookBreak };
    const totalHours = calculateRegularTotalHours(updatedRow, hospitalOptions);
    if (onTookBreakChange) {
      onTookBreakChange(rowIndex, { tookBreak, totalHours });
    } else {
      onChange(rowIndex, "tookBreak", tookBreak);
      onChange(rowIndex, "totalHours", totalHours);
    }
    if (tookBreak) {
      setMissedBreakPending({ rowIndex });
    }
  };

  const handleMissedBreakConfirm = (approverName) => {
    const { rowIndex } = missedBreakPending;
    const existingNotes = rows[rowIndex].notes || "";
    const approvalText = `Missed break approved by ${approverName}`;
    onChange(rowIndex, "notes", existingNotes ? `${existingNotes}\n${approvalText}` : approvalText);
    setMissedBreakPending(null);
  };

  const handleMissedBreakCancel = () => {
    if (!missedBreakPending) return;
    const { rowIndex } = missedBreakPending;
    const updatedRow = { ...rows[rowIndex], tookBreak: false };
    const totalHours = calculateRegularTotalHours(updatedRow, hospitalOptions);
    if (onTookBreakChange) {
      onTookBreakChange(rowIndex, { tookBreak: false, totalHours });
    } else {
      onChange(rowIndex, "tookBreak", false);
      onChange(rowIndex, "totalHours", totalHours);
    }
    setMissedBreakPending(null);
  };

  const handleLateBreakChange = (rowIndex, lateBreak) => {
    onChange(rowIndex, "lateBreak", lateBreak);
    if (lateBreak) {
      setLateBreakPending({ rowIndex });
    }
  };

  const handleLateBreakConfirm = (approverName) => {
    const { rowIndex } = lateBreakPending;
    const existingNotes = rows[rowIndex].notes || "";
    const approvalText = `Late break approved by ${approverName}`;
    onChange(rowIndex, "notes", existingNotes ? `${existingNotes}\n${approvalText}` : approvalText);
    setLateBreakPending(null);
  };

  const handleLateBreakCancel = () => {
    if (!lateBreakPending) return;
    const { rowIndex } = lateBreakPending;
    onChange(rowIndex, "lateBreak", false);
    setLateBreakPending(null);
  };

  return (
    <section className="sheet-section" aria-label="Regular hours">

      <RegularHoursTable
        rows={rows}
        onChange={onChange}
        onTimeChange={handleRegularTimeChange}
        onTookBreakChange={handleRegularTookBreakChange}
        onLateBreakChange={handleLateBreakChange}
        hospitalOptions={hospitalOptions}
        weekStartDate={weekStartDate}
        readOnly={readOnly}
        timeFormat={timeFormat}
      />
      <RegularMobileCards
        rows={rows}
        onChange={onChange}
        onTimeChange={handleRegularTimeChange}
        onTookBreakChange={handleRegularTookBreakChange}
        onLateBreakChange={handleLateBreakChange}
        hospitalOptions={hospitalOptions}
        weekStartDate={weekStartDate}
        readOnly={readOnly}
        setDayCardRef={setDayCardRef}
        timeFormat={timeFormat}
      />
      <BreakApproverDialog
        open={missedBreakPending !== null}
        title="Missed Break Approval"
        inputId="missed-break-approver-reg"
        onConfirm={handleMissedBreakConfirm}
        onCancel={handleMissedBreakCancel}
      />
      <BreakApproverDialog
        open={lateBreakPending !== null}
        title="Late Break Approval"
        inputId="late-break-approver-reg"
        onConfirm={handleLateBreakConfirm}
        onCancel={handleLateBreakCancel}
      />
    </section>
  );
}
